import React from 'react';

import { UserAvatar } from '@reckon-web/avatar';
import { Flex } from '@reckon-web/flex';
import { Text, TextProps } from '@reckon-web/text';
import { Tooltip } from '@reckon-web/tooltip';
import { SizeType } from '@reckon-web/avatar/src/Base';
import { makeId } from '@reckon-web/utils';

import { withUser } from '../../hocs/with-user';
import type { GetUserQueryItem } from '../../data/useGetUser';

type BaseNamedAvatarProps = {
  avatarSize?: SizeType;
  textSize?: TextProps['size'];
  textColor?: 'base' | 'inherit';
  textWeight?: TextProps['weight'];
  hideName?: boolean;
  hideAvatar?: boolean;
  prefix?: string;
  suffix?: string;
  nameSuffix?: string;
  nameOverride?: string;
  testId?: string;
};

type NamedUserAvatarProps = BaseNamedAvatarProps & {
  user: GetUserQueryItem | null;
};

type NamedAvatarProps = BaseNamedAvatarProps & {
  hasUser: boolean;
  userFullName?: string | null;
  userActive?: boolean | null;
  userLicensed?: boolean | null;
};

export const NamedAvatar: React.FC<NamedAvatarProps> = ({
  hasUser = true,
  userFullName,
  userActive,
  userLicensed,
  avatarSize = 'small',
  textSize = 'medium',
  textColor = 'inherit',
  textWeight = 'medium',
  hideName = false,
  hideAvatar = false,
  prefix = '',
  suffix = '',
  nameSuffix = '',
  nameOverride,
  testId = `named-user-avatar`,
}) => {
  if (hideName && hideAvatar) {
    console.warn(
      'Named User Avatar: both hideName and hideAvatar are false. Nothing will be rendered'
    );
    return null;
  }

  const name =
    nameOverride || (hasUser ? (userFullName ?? 'Unknown') : 'Unallocated');
  const isInactive = userActive === false;
  const isUnlicensed = userLicensed === false;
  const disabled = isInactive || isUnlicensed || !hasUser;
  const status = isUnlicensed ? '(unlicensed)' : isInactive ? '(inactive)' : '';

  if (hideName) {
    return (
      <Tooltip content={`${prefix} ${name} ${status} ${suffix}`}>
        <div>
          <UserAvatar
            name={userFullName ?? ''}
            size={avatarSize}
            disabled={disabled}
            data-testid={makeId(testId, 'tooltip-user-avatar')}
          />
        </div>
      </Tooltip>
    );
  }

  return (
    <Flex alignItems="center" gap={textSize}>
      {!hideAvatar && (
        <div>
          <UserAvatar
            name={userFullName ?? ''}
            size={avatarSize}
            disabled={disabled}
            data-testid={makeId(testId, 'user-avatar')}
          />
        </div>
      )}
      <Text
        size={textSize}
        weight={textWeight}
        leading="tighter"
        color={textColor}
        overflowStrategy="nowrap"
        data={{ testid: makeId(testId, 'name') }}
      >
        {name} {status} {nameSuffix}
      </Text>
    </Flex>
  );
};

export const NamedUserAvatar: React.FC<NamedUserAvatarProps> = ({
  user,
  ...rest
}) => {
  return (
    <NamedAvatar
      hasUser={Boolean(user)}
      userFullName={user?.fullName}
      userActive={user?.active}
      userLicensed={user?.licensed}
      {...rest}
    />
  );
};

export const NamedUserAvatarWithUser = withUser(NamedUserAvatar);
